import React, { useEffect } from 'react';
import T from 'mrf/utils/T';
import { useTranslation } from 'react-i18next';
import PageTitle from 'mrf/view/Title';
import { Grid, Col } from 'mrf/view/Grid';
import Paper from 'mrf/view/Paper';
import Button from 'mrf/view/Button';
import Separator from 'styles/basic/Separator';
import Select from 'mrf/view/Select';
import Flex from 'mrf/layout/Flex';
import { daoGet, daoPut } from 'mrf/Dao';
import {
    TYPE_GET_LIST,
    useDialogFormSelector,
    useFetcherSelector,
    useFormDataSelector,
    useFormSelector,
} from 'mrf/redux/ReduxHelper';
import EditUserProfileDialogForm from './dialogForms/EditUserProfileDialogForm';
import ChangePasswordDialogForm from './dialogForms/ChangePasswordDialogForm';
import ChangeEmailDialogForm from './dialogForms/ChangeEmailDialogForm';
import useCurrentUser from 'mrf/utils/useCurrentUser';
import IF from "./IF";
import mrfConfig from "../../mrf.config";
import EnableTOTPFormDialog from "./dialogForms/EnableTOTPForm";
import ELSE from "./ELSE";
import GenerateTOTPCodesForm from "./dialogForms/GenerateTOTPCodesForm";
import RemoveTOTPForm from "./dialogForms/RemoveTOTPForm";
import { clearAuthDataAction, setAuthDataAction } from 'mrf/redux/actions/auth';
import { useDispatch } from 'react-redux';

export const languageMap = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
];

export default function UserProfileTemplate({
    children,
    languages = languageMap,
}) {
    const dispatch = useDispatch();

    const user = useCurrentUser();

    const reloadUser = async () => {
        try {
            const loggedUser = await daoGet(`/api/users/loggedUser`);
            dispatch(clearAuthDataAction());
            dispatch(setAuthDataAction(loggedUser));
        } catch (error) {
            console.error("Fail to reload user.");
            console.error(error);
            return null;
        }
    }

    const roles = useFetcherSelector({
        selector: (state) => state?.resources?.roles,
        resource: 'roles',
        endpoint: '/api/roles',
        type: TYPE_GET_LIST,
    });

    const { t } = useTranslation();

    const setLanguage = async (data) => {
        try {
            await daoPut(`/api/Users/preferredLanguage/${data.language}`, {});
        } catch (e) {
            console.error(e);
        }
        reloadUser();
    };

    const languageContext = useFormSelector({
        resource: 'language',
        blankCondition: () => true,
        blankForm: () => ({ language: user?.language }),
    });

    const data = useFormDataSelector(languageContext);

    useEffect(() => {
        if (data?.language && data?.language !== user?.language) {
            setLanguage(data);
        }
    }, [data?.language]); // eslint-disable-line

    const changePasswordContext = useDialogFormSelector({
        resource: `changePasswordForm`,
    });

    const enableTOTPContext = useDialogFormSelector({
        resource: `enableTOTPForm`,
    });

    const generateTOTPContext = useDialogFormSelector({
        resource: `generateTOTPCodesForm`,
    });
    
    const removeTOTPContext = useDialogFormSelector({
        resource: `removeTOTPForm`
    });
    
    return (
        <Paper className='max-w-screen-lg mx-auto mt-5'>
            <PageTitle disableTranslation>{user?.userName}</PageTitle>
            <Grid>
                <Col size={12}>
                    <Separator />
                </Col>
                <Col size={6} justify='end' className='font-bold'>
                    <T>Full Name</T>:
                </Col>
                <Col size={6}>
                    <Flex>
                        <p>{user?.fullName}</p>
                        <EditUserProfileDialogForm
                            label='Full Name'
                            field='fullName'
                            initField={user?.fullName}
                            endpoint={`api/users/fullName`}
                            submitCb={reloadUser}
                        />
                    </Flex>
                </Col>
                <Col size={12}>
                    <Separator />
                </Col>
                <Col size={6} justify='end' className='font-bold'>
                    <T>Roles</T>:
                </Col>
                <Col size={6}>
                    <Flex gap={1}>
                        {user?.roles?.map((role, i) => (
                            <React.Fragment key={i}>
                                <T>{roles?.byId?.[role.roleId]?.name}</T><br />
                            </React.Fragment>
                        ))}
                    </Flex>
                </Col>
                <Col size={12}>
                    <Separator />
                </Col>
                <Col size={6} justify='end' className='font-bold'>
                    <T>E-mail</T>:
                </Col>
                <Col size={6}>
                    <Flex>
                        <p>{user?.email}</p>
                        <ChangeEmailDialogForm
                            endpoint={`api/users/changeEmail`}
                            submitCb={reloadUser}
                        />
                    </Flex>
                </Col>

                {children}

                <Col size={12}>
                    <Separator />
                </Col>
                <Col size={6} justify='end' className='font-bold'>
                    <p><T>Preferred Language</T>:</p>
                </Col>
                <Col size={6}>
                    <Select
                        size='small'
                        field='language'
                        width='auto'
                        context={languageContext}
                    >
                        {languages?.map((language, idx) => (
                            <option key={idx} value={language?.code?.toLowerCase()}>
                                {t(language?.name)}
                            </option>
                        ))}
                    </Select>
                </Col>
                <Col size={12}>
                    <Separator />
                </Col>

                <Col size={12} justify='center'>
                    <Button
                        color='primary'
                        onClick={changePasswordContext.open}
                    >
                        Change Password
                    </Button>
                    <ChangePasswordDialogForm
                        context={changePasswordContext}
                        endpoint={`api/users/changePassword`}
                    />
                </Col>
                <Col size={12}>
                    <Separator />
                </Col>
                <IF condition={!!mrfConfig.enableTOTP}>
                    <IF condition={!user?.isTwoFactorEnabled}>
                        <Col size={12} justify='center'>
                            <Button
                                color='primary'
                                onClick={enableTOTPContext.open}
                            >
                                Enable Two Factor Authentication
                            </Button>
                            <EnableTOTPFormDialog
                                context={enableTOTPContext}
                                endpoint={`api/users/enableTotp`}
                                submitCb={reloadUser}
                            />
                        </Col>
                    </IF>
                    <ELSE condition={!user?.isTwoFactorEnabled}>
                        <Col size={12} justify={'center'}>
                            <Button
                                color={'primary'}
                                onClick={generateTOTPContext.open}
                            >
                                Generate Two Factor Authentication Recovery Codes
                            </Button>
                            <GenerateTOTPCodesForm
                                context={generateTOTPContext}
                            />
                        </Col>
                        <Col size={12} justify={'center'}>
                            <Button
                                color={'delete'}
                                onClick={removeTOTPContext.open}
                            >
                                Remove the two-factor authentication from your account
                            </Button>
                            <RemoveTOTPForm
                                context={removeTOTPContext}
                                endpoint={`api/users/removeTotp`}
                                submitCb={reloadUser}
                            />
                        </Col>
                    </ELSE>
                </IF>
            </Grid>
        </Paper>
    );
}
